import Editor from '@toast-ui/editor';
import 'emoji-picker-element';

const FONTS = [
  'Georgia, serif',
  "Garamond, 'Times New Roman', serif",
  'Verdana, sans-serif',
  'Helvetica, Arial, sans-serif',
  "'Courier New', monospace",
  "Georgia, 'Times New Roman', serif",
  "'Times New Roman', serif",
  'Tahoma, sans-serif',
];

Editor.setLanguage('es', {
  Markdown: 'Markdown',
  WYSIWYG: 'WYSIWYG',
  Write: 'Escribir',
  Preview: 'Vista previa',
  Headings: 'Encabezados',
  Paragraph: 'Párrafo',
  Bold: 'Negrita',
  Italic: 'Cursiva',
  Strike: 'Tachado',
  Code: 'Código',
  Line: 'Línea',
  Blockquote: 'Cita',
  'Unordered list': 'Lista desordenada',
  'Ordered list': 'Lista ordenada',
  Task: 'Tarea',
  Indent: 'Sangría',
  Outdent: 'Disminuir sangría',
  'Insert link': 'Insertar enlace',
  'Insert CodeBlock': 'Insertar bloque de código',
  'Insert table': 'Insertar tabla',
  'Insert image': 'Insertar imagen',
  Heading: 'Encabezado',
  'Image URL': 'URL de la imagen',
  'Select image file': 'Seleccionar archivo de imagen',
  'Choose a file': 'Escoge un archivo',
  'No file': 'Ningún archivo',
  Description: 'Descripción',
  OK: 'Aceptar',
  More: 'Más',
  Cancel: 'Cancelar',
  File: 'Archivo',
  URL: 'URL',
  'Link text': 'Texto del enlace',
  'Add row to up': 'Añadir fila arriba',
  'Add row to down': 'Añadir fila abajo',
  'Add column to left': 'Añadir columna a la izquierda',
  'Add column to right': 'Añadir columna a la derecha',
  'Remove row': 'Eliminar fila',
  'Remove column': 'Eliminar columna',
  'Align column to left': 'Alinear a la izquierda',
  'Align column to center': 'Centrar',
  'Align column to right': 'Alinear a la derecha',
  'Remove table': 'Eliminar tabla',
  'Would you like to paste as table?': '¿Desea pegar como tabla?',
  'Text color': 'Color del texto',
  'Auto scroll enabled': 'Desplazamiento automático habilitado',
  'Auto scroll disabled': 'Desplazamiento automático deshabilitado',
  'Choose language': 'Elegir idioma',
});

function makeButton({ label, icon, title }) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'toastui-editor-toolbar-icons custom-toolbar-btn';
  btn.title = title;
  if (icon) {
    const svg = document.createElement('span');
    svg.className = 'tui-ico-custom';
    svg.textContent = label;
    btn.appendChild(svg);
  } else {
    btn.textContent = label;
  }
  return btn;
}

export function setupEditor({ container, pendingImages, onImport, onChange }) {
  const editor = new Editor({
    el: container,
    height: 'auto',
    initialEditType: 'markdown',
    previewStyle: 'vertical',
    language: 'es',
    theme: 'dark',
    hideModeSwitch: false,
    useCommandShortcut: true,
    toolbarItems: [
      ['heading', 'bold', 'italic', 'strike'],
      ['hr', 'quote', 'ul', 'ol', 'task'],
      ['table', 'link', 'code', 'codeblock'],
      ['indent', 'outdent'],
    ],
  });

  editor.changePreviewStyle('vertical');

  const fontSelect = document.createElement('select');
  fontSelect.className = 'font-select';
  fontSelect.title = 'Tipografía del artículo';
  fontSelect.appendChild(new Option('Tipografía del artículo', ''));
  for (const f of FONTS) fontSelect.appendChild(new Option(f, f));
  fontSelect.addEventListener('change', () => {
    const root = container.querySelector('.toastui-editor-defaultUI');
    if (root) root.style.fontFamily = fontSelect.value || '';
  });

  const fontItem = { name: 'fontFamily', el: fontSelect };

  const emojiBtn = makeButton({ label: '😀', title: 'Insertar emoji' });
  const emojiPicker = document.createElement('emoji-picker');
  emojiPicker.className = 'emoji-popover';
  emojiPicker.hidden = true;
  emojiBtn.addEventListener('click', () => {
    const rect = emojiBtn.getBoundingClientRect();
    emojiPicker.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - 340))}px`;
    emojiPicker.style.top = `${rect.bottom + 4}px`;
    emojiPicker.hidden = !emojiPicker.hidden;
  });
  emojiPicker.addEventListener('emoji-click', (e) => {
    editor.insertText(e.detail.unicode);
    emojiPicker.hidden = true;
  });

  const imageInput = document.createElement('input');
  imageInput.type = 'file';
  imageInput.accept = 'image/*';
  imageInput.style.display = 'none';

  const imageBtn = makeButton({ label: '🖼', title: 'Insertar imagen' });
  imageBtn.addEventListener('click', () => imageInput.click());

  const importInput = document.createElement('input');
  importInput.type = 'file';
  importInput.accept = '.pdf,.docx,.txt,.md,.markdown';
  importInput.style.display = 'none';

  const importBtn = makeButton({ label: '⬇', title: 'Importar archivo (PDF/DOCX/TXT/MD)' });
  importBtn.addEventListener('click', () => importInput.click());

  importInput.addEventListener('change', async () => {
    const file = importInput.files[0];
    importInput.value = '';
    if (file) {
      await onImport(file, editor);
    }
  });

  imageInput.addEventListener('change', async () => {
    const file = imageInput.files[0];
    imageInput.value = '';
    if (!file) return;
    const item = await pendingImages.addFile(file);
    editor.insertImage({ imageUrl: item.dataUri, altText: file.name.replace(/\.[^.]+$/, '') });
    if (onChange) onChange();
  });

  editor.insertToolbarItem({ groupIndex: 3, itemIndex: 0 }, { name: 'import', el: importBtn });
  editor.insertToolbarItem({ groupIndex: 3, itemIndex: 1 }, { name: 'image', el: imageBtn });
  editor.insertToolbarItem({ groupIndex: 3, itemIndex: 2 }, { name: 'emoji', el: emojiBtn });
  editor.insertToolbarItem({ groupIndex: 3, itemIndex: 3 }, { name: 'fontFamily', el: fontSelect });

  document.body.appendChild(emojiPicker);

  return {
    editor,
    getMarkdown: () => editor.getMarkdown(),
    setMarkdown: (md) => editor.setMarkdown(md || '', false),
    getFontFamily: () => fontSelect.value,
    setFontFamily: (f) => {
      fontSelect.value = FONTS.includes(f) ? f : '';
      const root = container.querySelector('.toastui-editor-defaultUI');
      if (root) root.style.fontFamily = fontSelect.value || '';
    },
    insertText: (t) => editor.insertText(t),
  };
}
